import {
  AloftPrice,
  CraftsmanCostProps,
  PositiveInt,
  getCraftsmanCost,
  isUndefined,
} from '@aloft/toolkit-shared';

import { selectImprovementsEntities } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { createFeatureCostSelectors } from '../utils/createFeatureCostSelectors.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectCostProps } from './selectCostProps.ts';

// Bathrooms costs are included in the dwelling cost for SFR and Manufactured homes.
// This should only be used in multifamily residences for units with >1 bathroom (not applicable for MVP).
const calculateHalfBathCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
) => {
  const improvementsForTransaction = improvements[id];
  const identification = propertyIdentification[id];

  if (!improvementsForTransaction || !identification) return undefined;

  const {
    address: { PostalCode: zipCode },
  } = identification;
  const {
    halfBaths = 0 as PositiveInt,
    craftsmanQuality: quality,
    propertyType = 'singleFamilyResidence',
  } = improvementsForTransaction;

  if (isUndefined(quality) || (quality * 10) % 1 !== 0 || quality < 1 || isUndefined(zipCode)) {
    return undefined;
  }

  const calculator = getCraftsmanCost(costProps);

  return calculator.getHalfBathroomsCost({
    halfBaths,
    quality,
    propertyType,
    zipCode,
  });
};

export const {
  selectHalfBathCost,
  selectHalfBathCosts,
  selectSubjectHalfBathCostOutput,
  selectSubjectArvHalfBathCostOutput,
} = createFeatureCostSelectors({
  calculate: calculateHalfBathCost,
  inputSelectors: [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
  ],
  names: {
    cost: 'selectHalfBathCost',
    costs: 'selectHalfBathCosts',
    subjectOutput: 'selectSubjectHalfBathCostOutput',
    subjectArvOutput: 'selectSubjectArvHalfBathCostOutput',
  },
});

export const selectBaseHalfBathCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state, id: string) => id,
  ],
  (costProps, improvements, propertyIdentification, id) => {
    const costOutput = calculateHalfBathCost(costProps, improvements, propertyIdentification, id);
    if (costOutput === undefined) return AloftPrice.zero;
    return costOutput.multiplierBreakdown.none;
  },
);
